import React, { useMemo, useCallback, useState, useEffect } from 'react';
import PageHeader from '../../components/PageHeader';
import DataList from '../../components/DataList';
import { getUploadsTableConfig, PAGE_CONFIG } from './config';
import EmptyTableIndicator from '../../components/EmptyTableIndicator';
import { faFileContract } from '@fortawesome/free-solid-svg-icons';
import ActionToolbar from '../../components/ActionToolbar';
import AddButton from '../../components/AddButton';
import UploadModal from './UploadModal';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../../context/ThemeProvider';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { useModals } from '../../hooks/useModals';
import {
    useCreateUpload,
    useUpdateUpload,
    useDeleteUpload,
    useUploadForm,
    useUploadActions
} from './hooks/index';
import api from '../../api';
import { resolveCatalogGroups } from '../../catalog/catalogGroups';

const Upload = () => {
    const { t } = useTranslation();
    const tr = useCallback((key) => t(`uploadAdmin:${key}`), [t]);

    const { theme } = useTheme();
    const queryClient = useQueryClient();

    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [selectedOptionId, setSelectedOptionId] = useState('all');

    const [params, setParams] = useState({
        page: 1,
        limit: 10,
        search: ''
    });

    const handlePageChange = (newPage) => {
        if (newPage < 1) return;
        setParams((prev) => ({ ...prev, page: newPage }));
    };

    const handlePageSizeChange = (limit) => {
        if (limit < 1) return;
        setParams((prev) => ({ ...prev, limit }));
    };

    const { data: materialTypes } = useQuery({
        queryKey: ['materialTypes'],
        queryFn: () => api.materialTypes.getMaterialTypes(),
        select: (data) => data?.data?.data
    });

    const catalogGroups = useMemo(() => resolveCatalogGroups(materialTypes || []), [materialTypes]);
    const selectedGroup = catalogGroups.find((group) => group.id === selectedGroupId) || catalogGroups[0] || null;
    const selectedOption =
        selectedOptionId !== 'all' ? selectedGroup?.options.find((option) => option.id === selectedOptionId) : null;

    const queryMaterialTypeId = selectedOption?.queryId || selectedGroup?.queryId || null;

    const queryParams = useMemo(
        () => ({
            ...params,
            materialTypeId: queryMaterialTypeId
        }),
        [params, queryMaterialTypeId]
    );

    const { data: dataResponse, isLoading } = useQuery({
        queryKey: ['data', queryParams],
        queryFn: () => api.upload.getUploads(queryParams),
        enabled: !!queryMaterialTypeId,
        select: (data) => data?.data
    });

    const data = dataResponse?.data;
    const pagination = {
        page: params.page,
        limit: params.limit,
        totalCount: dataResponse?.totalCount || 0
    };

    const { isOpen, isDeleteOpen, selectedItem, handleOpenModal, handleCloseModal, handleOpenDeleteModal, handleCloseDeleteModal } = useModals();

    const { createUpload, createStatus } = useCreateUpload(queryClient, theme);
    const { updateUpload, updateStatus } = useUpdateUpload(queryClient, theme);
    const { deleteUpload, isDeleting } = useDeleteUpload(queryClient, theme, handleCloseDeleteModal);

    useEffect(() => {
        if (catalogGroups.length > 0 && !selectedGroupId) {
            setSelectedGroupId(catalogGroups[0].id);
        }
    }, [catalogGroups, selectedGroupId]);

    const handleGroupClick = useCallback((groupId) => {
        setSelectedGroupId(groupId);
        setSelectedOptionId('all');
        setParams((prev) => ({ ...prev, page: 1 }));
    }, []);

    const handleOptionClick = useCallback((optionId) => {
        setSelectedOptionId(optionId);
        setParams((prev) => ({ ...prev, page: 1 }));
    }, []);

    const { form, errors, isEdit, selectedItem: formSelectedItem, handleInputChange, handleFileChange, resetForm, setEditData, handleSubmit } = useUploadForm(
        createUpload,
        updateUpload,
        handleCloseModal,
        materialTypes,
        catalogGroups
    );

    const { downloadingId, handleEdit, handleDownload, truncateDescription: truncate } = useUploadActions(theme, handleOpenDeleteModal, setEditData);

    const defaultCreateForm = useMemo(() => {
        if (!selectedGroup) return {};
        const onlyOption = selectedGroup.options.length === 1 ? selectedGroup.options[0] : null;
        const materialTypeId =
            selectedOption?.types[0]?.id ||
            (!selectedGroup.showSubFilter && selectedGroup.types.length === 1 ? selectedGroup.types[0].id : '') ||
            (onlyOption?.types.length === 1 ? onlyOption.types[0].id : '');
        return {
            catalog_group_id: selectedGroup.id,
            material_type_id: materialTypeId
        };
    }, [selectedGroup, selectedOption]);

    const handleOpenCreateModal = useCallback(() => {
        handleOpenModal();
        resetForm(defaultCreateForm);
    }, [handleOpenModal, resetForm, defaultCreateForm]);

    const handleCloseModalWithReset = useCallback(() => {
        handleCloseModal();
        resetForm();
    }, [handleCloseModal, resetForm]);

    const handleEditWithModal = useCallback(
        (item) => {
            handleOpenModal();
            handleEdit(item);
        },
        [handleOpenModal, handleEdit]
    );

    const handleDeleteWithModal = useCallback(
        (item) => {
            handleOpenDeleteModal(item);
        },
        [handleOpenDeleteModal]
    );

    const columns = useMemo(
        () => getUploadsTableConfig(truncate, handleEditWithModal, handleDeleteWithModal, handleDownload, downloadingId),
        [truncate, handleEditWithModal, handleDeleteWithModal, handleDownload, downloadingId]
    );

    return (
        <div className="flex">
            <div className="flex-1">
                <PageHeader items={PAGE_CONFIG.BREADCRUMB} pageTitle={PAGE_CONFIG.PAGE_TITLE} />
                <ActionToolbar
                    searchQuery={params.search}
                    onSearchChange={(e) => setParams({ ...params, search: e.target.value, page: 1 })}
                    actionButton={<AddButton isLoading={false} onClick={handleOpenCreateModal} label={tr('create')} />}
                    disabled={false}
                />

                <div className="flex flex-wrap gap-2 mb-3">
                    {catalogGroups.map((group) => (
                        <button
                            type="button"
                            key={group.id}
                            className={clsx(
                                'p-2 rounded-md cursor-pointer transition-colors duration-200',
                                selectedGroup?.id === group.id
                                    ? 'bg-[#50A1CA] text-white dark:bg-[#50A1CA]'
                                    : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                            )}
                            onClick={() => handleGroupClick(group.id)}
                        >
                            {group.label}
                        </button>
                    ))}
                </div>

                {selectedGroup?.showSubFilter ? (
                    <div className="flex flex-wrap gap-2 mb-4">
                        <button
                            type="button"
                            className={clsx(
                                'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors duration-200',
                                selectedOptionId === 'all'
                                    ? 'bg-[#50A1CA] text-white border-[#50A1CA]'
                                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-[#50A1CA]'
                            )}
                            onClick={() => handleOptionClick('all')}
                        >
                            All
                        </button>
                        {selectedGroup.options.map((option) => (
                            <button
                                type="button"
                                key={option.id}
                                className={clsx(
                                    'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors duration-200',
                                    selectedOptionId === option.id
                                        ? 'bg-[#50A1CA] text-white border-[#50A1CA]'
                                        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-[#50A1CA]'
                                )}
                                onClick={() => handleOptionClick(option.id)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                ) : null}

                {data?.length > 0 ? (
                    <DataList data={data} columns={columns} pagination={pagination} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} />
                ) : (
                    <EmptyTableIndicator colSpan={5} isLoading={isLoading} icon={faFileContract} description={PAGE_CONFIG.SEARCH_DESCRIPTION} placeholder={PAGE_CONFIG.SEARCH_PLACEHOLDER} loadingMessage="Loading..." />
                )}
                <UploadModal
                    handleFileChange={handleFileChange}
                    handleChange={handleInputChange}
                    handleSubmit={handleSubmit}
                    catalogGroups={catalogGroups}
                    form={form}
                    isOpen={isOpen}
                    onClose={handleCloseModalWithReset}
                    status={createStatus}
                    isEdit={isEdit}
                    selectedItem={formSelectedItem}
                    setIsEdit={setEditData}
                    onSubmit={handleSubmit}
                    isLoading={createStatus === 'pending' || updateStatus === 'pending'}
                    errors={errors}
                />

                <ConfirmationDialog
                    title={tr('deleteHeader')}
                    onConfirm={() => deleteUpload(selectedItem?.id)}
                    type="DELETE"
                    message={tr('deleteMessage', { title: selectedItem?.title?.length > 50 ? selectedItem?.title?.substring(0, 50) + '...' : selectedItem?.title })}
                    isOpen={isDeleteOpen}
                    onClose={handleCloseDeleteModal}
                    isLoading={isDeleting}
                />
            </div>
        </div>
    );
};

export default Upload;
